import { memo, useId, useMemo, useState } from "react";
import {
  ALL_LAYERS,
  BASE_MAPS,
  LAYER_GROUP_LABEL,
  LENSES,
  type BaseMapId,
  type LayerGroup,
  type LayerId,
  type LensId,
} from "../map/presets";
import { ConnectorLayerGroup } from "./ConnectorLayerGroup";
import type { ConnectorLayerEntry } from "../map/connectorLayer";

// Plain names make the map-view choice understandable before selection.
const LENS_BOARD_NAME: Record<LensId, string> = {
  ioc: "Command",
  maintenance: "Maintenance",
  energy: "Energy (modeled)",
  mobility: "Mobility",
  xray: "Underground",
};

interface Props {
  lens: LensId;
  onLensChange: (l: LensId) => void;
  enabled: Set<LayerId>;
  onToggleLayer: (id: LayerId) => void;
  onClearAllLayers?: () => void;
  baseMap: BaseMapId;
  onBaseMapChange: (b: BaseMapId) => void;
  /**
   * Layers whose upstream feed currently has zero features, so switching them
   * on draws nothing. Without this the palette had no idea: an empty feed made
   * a toggle flip to "on" with no map change and no explanation — eleven POI
   * toggles at once whenever the shared Overpass feed was rate-limited. The
   * row now says so instead of leaving the user to wonder what broke.
   */
  emptyLayers?: Set<LayerId>;
  /** Registry-driven "Connected sources" group (ids "connector:<id>"). Omit to hide it. */
  connectorLayers?: readonly ConnectorLayerEntry[];
  enabledConnectorLayers?: ReadonlySet<string>;
  onToggleConnectorLayer?: (layerId: string) => void;
  emptyConnectorLayers?: ReadonlySet<string>;
}

const NO_CONNECTOR_LAYERS: ReadonlySet<string> = new Set();

// Layers whose values are computed, not measured. The palette says so on the
// row itself — the hover describe was the only disclosure before, and a label
// like "Building occupancy" reads as a sensor count without it.
const MODELED_LAYERS: ReadonlySet<LayerId> = new Set<LayerId>([
  "parking-capacity", "building-occupancy", "internal-traffic", "wifi-signal",
  "fire-hose", "trash-collection", "campus-energy", "cu-shuttle-vehicles",
]);

// Top-level hierarchy the user reads:
//   1. Map view (preset bundle of layers)
//   2. Base map (mutually exclusive ground style)
//   3. Layer groups (additive toggles)
// AQ lives inside "campus" — both station dots and the IQAir heatmap.
const GROUP_ORDER: LayerGroup[] = ["campus", "operations", "mobility", "incidents", "utility", "environment", "xray"];

// Plain-language hover hint per layer group — the group names alone
// ("Imagery", "Pratumwan X-Ray") mean little to a first-time viewer.
const GROUP_HINT: Record<LayerGroup, string> = {
  campus: "On-campus data — buildings, boundary, gates, air quality",
  operations: "Modeled ops picture — parking, occupancy, internal traffic, WiFi, fire hose, bins",
  mobility: "How people move — shuttle buses, trains, road speed, CCTV",
  incidents: "Problems reported right now — accidents, complaints, roadworks",
  utility: "Power, water, drainage and energy use on campus",
  environment: "Trees, canopy and flood gauges",
  xray: "What's underground — tunnels, pipes, and cables beneath Pratumwan",
};

// memo: App re-renders per RAF frame during camera moves; palette props are
// referentially stable (useCallback handlers + Set identity), so skip those.
function LayerPaletteImpl({
  lens,
  onLensChange,
  enabled,
  onToggleLayer,
  onClearAllLayers,
  baseMap,
  onBaseMapChange,
  emptyLayers,
  connectorLayers,
  enabledConnectorLayers,
  onToggleConnectorLayer,
  emptyConnectorLayers,
}: Props) {
  // group → list of layers (preserve declaration order within each group)
  const grouped = useMemo(() => {
    const m = new Map<LayerGroup, typeof ALL_LAYERS>();
    for (const g of GROUP_ORDER) m.set(g, []);
    for (const l of ALL_LAYERS) m.get(l.group)?.push(l);
    return m;
  }, []);

  const choiceId = useId();
  const [collapsed, setCollapsed] = useState<Record<LayerGroup, boolean>>({
    campus: true, operations: true, mobility: true, incidents: true,
    utility: true, environment: true, xray: true,
  });
  const toggleGroup = (g: LayerGroup) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  // Hovered/focused layer → surface its purpose in a visible explainer below,
  // instead of hiding it in a title tooltip.
  const [hovered, setHovered] = useState<{ label: string; describe: string } | null>(null);

  const enabledByGroup = (g: LayerGroup) =>
    (grouped.get(g) ?? []).filter((l) => enabled.has(l.id)).length;

  return (
    <div className="col">
      <div className="map-choice-group">
        <label className="map-choice" htmlFor={`${choiceId}-view`}>Map view
          <select id={`${choiceId}-view`} value={lens}
            onChange={(event) => onLensChange(event.target.value as LensId)}>
            {LENSES.filter(l => !l.hideFromPalette).map(l =>
              <option key={l.id} value={l.id}>{LENS_BOARD_NAME[l.id]} · {l.label}</option>)}
          </select>
        </label>
        <label className="map-choice" htmlFor={`${choiceId}-ground`}>Base map
          <select id={`${choiceId}-ground`} value={baseMap}
            onChange={(event) => onBaseMapChange(event.target.value as BaseMapId)}>
            {BASE_MAPS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </label>
        {(baseMap === "night" || baseMap === "true-color") &&
          <p className="caption map-choice-note">Regional satellite imagery — soft at campus zoom, not a live camera view.</p>}
        <details className="map-choice-help"><summary>About this view and base map</summary>
          <p>{LENSES.find(l => l.id === lens)?.describe}</p>
          <p>{BASE_MAPS.find(b => b.id === baseMap)?.describe}</p>
        </details>
      </div>
      <div>
        <div className="layer-controls-head">
          <span className="eyebrow">Layers</span>
          <button type="button" className="layer-clear-btn" aria-label="Clear all overlays"
            title="Turn off all data layers" onClick={onClearAllLayers ?? (() => {
              for (const id of Array.from(enabled)) onToggleLayer(id);
            })}>Clear All</button>
        </div>
        <div className="layer-groups">
          {GROUP_ORDER.map((g) => {
            const list = grouped.get(g) ?? [];
            if (list.length === 0) return null;
            const on = enabledByGroup(g);
            const isCollapsed = collapsed[g];
            return (
              <section key={g} className={`layer-group ${isCollapsed ? "is-collapsed" : ""}`}>
                <button
                  type="button"
                  className="layer-group-head"
                  onClick={() => toggleGroup(g)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${LAYER_GROUP_LABEL[g]} layers (${on} of ${list.length} on)`}
                  title={GROUP_HINT[g]}
                >
                  <span className="layer-group-name mono">{LAYER_GROUP_LABEL[g]}</span>
                  <span className="layer-group-meta mono">{on}/{list.length}</span>
                  <span className="layer-group-chevron mono">{isCollapsed ? "▶" : "▼"}</span>
                </button>
                {!isCollapsed && (
                  <div className="layer-toggles">
                    {list.map((l) => {
                      const isOn = enabled.has(l.id);
                      // Only worth saying while the layer is on — an "off"
                      // toggle drawing nothing is not a surprise.
                      const isEmpty = isOn && (emptyLayers?.has(l.id) ?? false);
                      const emptyNote = isEmpty
                        ? " — no data in this feed right now, so nothing draws on the map"
                        : "";
                      return (
                        <div
                          key={l.id}
                          className={`layer-toggle ${isOn ? "on" : "off"}${isEmpty ? " is-empty" : ""}`}
                          role="checkbox"
                          aria-checked={isOn}
                          tabIndex={0}
                          title={`${l.describe}${emptyNote}`}
                          aria-label={`${l.label} — ${l.describe}${emptyNote}`}
                          onMouseEnter={() => setHovered({ label: l.label, describe: l.describe })}
                          onMouseLeave={() => setHovered(null)}
                          onFocus={() => setHovered({ label: l.label, describe: l.describe })}
                          onBlur={() => setHovered(null)}
                          onClick={() => onToggleLayer(l.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onToggleLayer(l.id);
                            }
                          }}
                        >
                          <span className="row">
                            <span className="swatch" style={{ background: l.swatch }} />
                            <span>{l.label}</span>
                            {MODELED_LAYERS.has(l.id) && (
                              <span
                                className="layer-ai-badge"
                                title="Modeled from real campus geometry — scenario tier, not a sensor reading. Swaps to live when a feed lands."
                              >
                                SIM
                              </span>
                            )}
                          </span>
                          <span className="mono caption">
                            {isEmpty ? "no data" : isOn ? "on" : "off"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
          {connectorLayers && onToggleConnectorLayer && (
            <ConnectorLayerGroup
              entries={connectorLayers}
              enabled={enabledConnectorLayers ?? NO_CONNECTOR_LAYERS}
              onToggle={onToggleConnectorLayer}
              emptyLayers={emptyConnectorLayers}
              onHover={setHovered}
            />
          )}
        </div>
        <div className="layer-explainer" role="status" aria-live="polite">
          {hovered ? (
            <><strong>{hovered.label}</strong> — {hovered.describe}</>
          ) : (
            <span className="hint">Layers add extra data on top of the map. Point at one to see what it shows.</span>
          )}
        </div>
      </div>
    </div>
  );
}

export const LayerPalette = memo(LayerPaletteImpl);
